import React, {useState} from 'react';
import {compose, graphql} from 'react-apollo';
import gql from 'graphql-tag';
import {Redirect} from '@reach/router';
import {Elements} from 'react-stripe-elements';

import {Card, Loading} from 'ui-kit';

import {
  AddMember,
  ContactDetailsCard,
  Member,
  PaymentDetailsCard,
  PaymentDetailsModal,
} from './components';
import * as styles from './MembershipDetail.module.css';

function MembershipDetail({data, id}) {
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  if (data == null || data.error) {
    console.error(data.error);
    return (
      <Card>
        <p>An error occurred. Try refreshing this page or come back later.</p>
      </Card>
    );
  }

  if (data.loading) {
    return <Loading />;
  }

  const membership = data.me.practice.membershipV2;

  if (membership == null) {
    return <Redirect to="/memberships" noThrow />;
  }

  const primaryAccountHolder = membership.memberV2s.find(
    member => member.primaryAccountHolder,
  );

  return (
    <>
      <div className={styles.Layout}>
        <div>
          <Card>
            <h3 className={styles.CardHeading}>Members</h3>
            <div className={styles.MembersContainer}>
              {membership.memberV2s.map(member => (
                <Member
                  member={member}
                  key={member.id}
                  membershipPlans={data.me.practice.membershipPlans.edges.map(
                    ({node}) => node,
                  )}
                  hasPaymentSource={membership.slowHasPaymentSource}
                  sourceLast4={membership.slowPrimaryPaymentSourceLast4}
                  openPaymentModal={() => setPaymentModalOpen(true)}
                  onBenefitUseSubmit={() => data.refetch()}
                  onEditPlanSubmit={async () => await data.refetch()}
                />
              ))}
            </div>

            <div className={styles.NewMemberContainer}>
              <AddMember membershipId={id} onSubmit={() => data.refetch()} />
            </div>
          </Card>
        </div>

        <div>
          <ContactDetailsCard
            email={primaryAccountHolder.email}
            phone={primaryAccountHolder.phone}
          />

          <div className={styles.PaymentDetailsCard}>
            <PaymentDetailsCard
              cardLast4={membership.slowPrimaryPaymentSourceLast4}
              openPaymentModal={() => setPaymentModalOpen(true)}
            />
          </div>
        </div>
      </div>
      <Elements>
        <PaymentDetailsModal
          id={id}
          show={paymentModalOpen}
          onDismiss={() => setPaymentModalOpen(false)}
          onSubmit={() => {
            data.refetch();
            setPaymentModalOpen(false);
          }}
        />
      </Elements>
    </>
  );
}

export default compose(
  graphql(
    gql`
      query MembershipDetail($id: ID!) {
        me {
          ... on StaffMember {
            id
            practice {
              id

              membershipPlans(first: 50) {
                edges {
                  node {
                    id
                    title
                    priceCents
                  }
                }
              }

              membershipV2(id: $id) {
                id
                slowHasPaymentSource
                slowPrimaryPaymentSourceLast4
                memberV2s {
                  id
                  email
                  phone
                  firstName
                  lastName
                  primaryAccountHolder
                  membershipPlanSubscriptions {
                    id
                    status
                    startDate
                    endDate
                    benefitUses {
                      id
                      performedAt
                      benefit {
                        id
                      }
                    }
                    membershipPlan {
                      id
                      title
                      benefits {
                        id
                        name
                        quantity
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    {
      options: {
        fetchPolicy: 'network-only',
      },
    },
  ),
)(MembershipDetail);
